import { apiClient } from '@/auth';

/**
 * Generic key-value app-data store, scoped per patient. Used for
 * clinician/patient preferences that don't have first-class endpoints —
 * notably the per-patient glucose-range overrides driving the CGM card.
 *
 * Endpoints from MNT swagger:481-560:
 *   GET    /patients/{id}/app-data/{platform}/{scope}/{name}
 *   PUT    /patients/{id}/app-data/{platform}/{scope}/{name}
 *   DELETE /patients/{id}/app-data/{platform}/{scope}/{name}
 *
 * v1 mirrors this on top of localStorage for offline support
 * (PatientAppDataService.ts). V2 stays server-only — simpler, and the
 * use cases we have today don't need offline.
 */

export type AppDataScope = 'cgm' | 'ghp';
export type AppDataPlatform = 'general' | 'mobile';

export type AppDataValue<T> = {
  value: T | null;
  updated_time?: string | null;
};

export const patientAppDataApi = {
  async get<T>(
    patientId: number,
    platform: AppDataPlatform,
    scope: AppDataScope,
    name: string,
  ): Promise<AppDataValue<T>> {
    try {
      const res = await apiClient.get<AppDataValue<T>>(
        `/patients/${patientId}/app-data/${platform}/${scope}/${name}`,
      );
      return res.data ?? { value: null };
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // 404 = no value stored yet — treat as empty rather than throwing.
      if (status === 404) return { value: null };
      throw err;
    }
  },

  async put<T>(
    patientId: number,
    platform: AppDataPlatform,
    scope: AppDataScope,
    name: string,
    body: T,
  ): Promise<AppDataValue<T>> {
    const res = await apiClient.put<AppDataValue<T>>(
      `/patients/${patientId}/app-data/${platform}/${scope}/${name}`,
      body,
    );
    return res.data ?? { value: null };
  },

  async delete(
    patientId: number,
    platform: AppDataPlatform,
    scope: AppDataScope,
    name: string,
  ): Promise<void> {
    await apiClient.delete(
      `/patients/${patientId}/app-data/${platform}/${scope}/${name}`,
    );
  },
};
